/**
 * Which top-level sections `/whats-on` is currently rendering, in render order.
 *
 * Drives the page's sticky jump-nav. Pure and unit-tested for the same reason
 * as `whatsOnBuckets.js` and `whatsOnTeaser.js`: the nav renders exactly what
 * this returns, so "which chips exist" is verifiable without mounting anything.
 *
 * **The honesty rule.** A chip may only exist for a section that is actually on
 * the page. A nav that offers to jump somewhere and then does not is worse than
 * no nav — so the presence tests here must mirror the page's own render guards
 * exactly. They are duplicated deliberately rather than derived, because the
 * page's guards are JSX conditions; if you change one, change the other.
 *
 * Nothing enforces that pairing automatically. `whatsOnSections.test.js` pins
 * THIS function's behaviour only -- it never renders the page, so a guard that
 * changes on the JSX side alone leaves every test green while the nav starts
 * lying. Check both sides by hand, or the honesty rule is only a comment.
 *
 * Render order is 1, 2, 4, 3 — the book-ahead band sits above "near you"
 * (see the header comment in `src/pages/WhatsOn.js`).
 */

// Below this, the nav is noise: a single chip cannot help you navigate, and
// two is the point where a jump starts to beat a scroll.
export const MIN_SECTIONS_FOR_NAV = 2;

// `id` is the DOM id AND the scroll target; `labelKey` resolves under
// `whatsOn.sectionNav.` in the `details` catalog.
const SECTION_IDS = {
  newly: 'whats-on-newly',
  upcoming: 'whats-on-upcoming',
  bookAhead: 'whats-on-book-ahead',
  near: 'whats-on-near'
};

export const sectionDomId = (key) => SECTION_IDS[key] || null;

/**
 * How many holiday sub-sections the book-ahead band shows at once. Two: the
 * band is a nudge, not a calendar.
 */
export const MAX_ANCHOR_SECTIONS = 2;

/**
 * Which holidays the book-ahead band actually renders, soonest decision first.
 *
 * **Drop empties BEFORE capping, never after.** This is the whole function, and
 * it exists because the page had it the other way round: `upcomingAnchors` was
 * asked for exactly 2 anchors and the page rendered whichever of those had
 * content. Anchors are ordered by date and chosen before anything knows what is
 * in them, so an empty holiday could take a slot from a full one — and after
 * the 2026-09-11 theming change, empty holidays became the common case rather
 * than the exception.
 *
 * Measured on the dev DB, same Los Angeles coordinates, only the viewer's
 * country changed:
 *
 *   country ''    → Halloween(2) + Christmas(7)  = 9 cards
 *   country 'US'  → Halloween(2) + Thanksgiving(0)  = 2 cards
 *
 * Thanksgiving has zero themed events in the entire corpus, and it deleted
 * Christmas's seven. A London/GB viewer had it worse: Halloween(0) and Guy
 * Fawkes(0) took both slots and the band vanished, while 13 Christmas cards sat
 * one anchor further down.
 *
 * So the caller asks `upcomingAnchors` for MORE candidates than it will show
 * (see `ANCHOR_CANDIDATE_LIMIT` in `WhatsOn.js`), fetches them in parallel, and
 * hands them all here. Ordering is preserved throughout, which is what keeps
 * this safe: an item is claimed by the earliest anchor that matched it, and the
 * cap keeps the earliest sections, so capping can never orphan an item that a
 * surviving section would have shown.
 *
 * @param {Array<{key: string}>} anchors      candidates, soonest decision first
 * @param {Object<string, Array>} itemsByAnchor  anchor key → its items
 * @param {object} [opts]
 * @param {number} [opts.limit=MAX_ANCHOR_SECTIONS]
 * @returns {Array<{anchor: object, items: Array}>} only non-empty, capped
 */
export function pickAnchorSections(anchors, itemsByAnchor, { limit = MAX_ANCHOR_SECTIONS } = {}) {
  if (!Array.isArray(anchors)) return [];
  const byAnchor = itemsByAnchor || {};
  return anchors
    .map(anchor => ({ anchor, items: byAnchor[anchor.key] || [] }))
    .filter(section => section.items.length > 0)
    .slice(0, limit);
}

/**
 * @param {object}  s
 * @param {boolean} s.loading        bands request in flight
 * @param {boolean} s.error          bands request failed
 * @param {Array}   s.newlyGroups    band 1's VISIBLE groups (dismissal-filtered)
 * @param {Array}   s.upcomingItems  band 2's VISIBLE items
 * @param {Array}   s.anchorSections band 4's non-empty per-anchor sections
 * @param {Array}   s.nearItems      band 3's items
 * @param {boolean} s.nearLoading    band 3's own independent fetch
 * @param {boolean} s.anchorsPending a book-ahead chip is still possible but
 *   its fetch has not come back yet
 * @returns {Array<{key, id, labelKey, count}>} count is null when unknown or
 *   zero — a chip reading "Near you 0" over an empty state is a worse lie than
 *   no number at all.
 */
export function buildSectionList({
  loading = false,
  error = false,
  newlyGroups = [],
  upcomingItems = [],
  anchorSections = [],
  nearItems = [],
  nearLoading = false,
  anchorsPending = false
} = {}) {
  // While either request that decides the SHAPE of the nav is outstanding we
  // do not yet know which sections will exist. Rendering now means a nav that
  // changes shape under the user's finger a moment later, which is worse than
  // one that arrives late.
  //
  // `anchorsPending` is the second of those and was missed the first time:
  // the book-ahead fetch resolves a round trip after `loading` clears, so the
  // nav painted [New, Coming up, Near you] and then spliced "Book ahead" into
  // the middle, shoving "Near you" sideways under a reaching finger. Holiday
  // lead windows cover roughly half the year, so that was the common path,
  // not an edge case.
  if (loading || anchorsPending) return [];

  const out = [];
  const push = (key, count) => out.push({
    key,
    id: SECTION_IDS[key],
    labelKey: `whatsOn.sectionNav.${key}`,
    count: count > 0 ? count : null
  });

  // Band 1 always renders — it owns the page's empty AND error states, so it
  // is on the page even with nothing in it.
  push('newly', newlyGroups.reduce((n, g) => n + (g.cardItems?.length || 0), 0));

  // Bands 2 and 4 render only when non-empty (the page hides them entirely
  // rather than showing a header over nothing).
  if (!error && upcomingItems.length > 0) push('upcoming', upcomingItems.length);
  if (anchorSections.length > 0) {
    push('bookAhead', anchorSections.reduce((n, sec) => n + (sec.items?.length || 0), 0));
  }

  // Band 3 renders unless the bands request failed. Its count stays null while
  // its own fetch is still running — the section is there, the number is not.
  if (!error) push('near', nearLoading ? 0 : nearItems.length);

  return out;
}

export default buildSectionList;
